'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';

const categories = ['All', 'Product Updates', 'Traveler Tips', 'Sender Guides', 'Safety & Trust', 'Company News', 'Kenya Logistics'];

const posts = [
  {
    id: 'how-to-earn-5000-per-trip',
    category: 'Traveler Tips',
    title: 'How Verified MuyuRide Travelers Earn KES 3,000–5,000 Extra Per Trip',
    excerpt: 'You\'re already traveling from Nairobi to Mombasa. Your bag has space. Here\'s the exact playbook our top-earning travelers use to consistently earn KES 3,000–5,000 per trip — without changing their plans.',
    author: 'Dorcas Wona Ndinya',
    authorRole: 'Head of Growth',
    authorInitials: 'DW',
    authorColor: 'bg-green-600',
    date: 'July 10, 2026',
    readTime: '7 min read',
    featured: true,
    tags: ['Earnings', 'Traveler', 'Tips'],
    accentColor: 'from-amber-500/20 to-amber-500/5',
    borderColor: 'border-amber-200',
  },
  {
    id: 'escrow-explained',
    category: 'Sender Guides',
    title: 'MuyuRide Escrow Explained: Why Your Money Is Always Safe',
    excerpt: 'Worried about paying a stranger to carry your parcel? Don\'t be. Here\'s exactly how our escrow system works — and why 31,400+ senders trust it with their deliveries.',
    author: 'Martha Wangui',
    authorRole: 'Head of Trust & Safety',
    authorInitials: 'MW',
    authorColor: 'bg-[#F59E0B]',
    date: 'July 8, 2026',
    readTime: '5 min read',
    featured: false,
    tags: ['Payments', 'Safety', 'Escrow'],
    accentColor: 'from-blue-500/20 to-blue-500/5',
    borderColor: 'border-blue-200',
  },
  {
    id: 'nairobi-mombasa-route-guide',
    category: 'Kenya Logistics',
    title: 'The Nairobi–Mombasa Route: Kenya\'s Busiest Delivery Corridor',
    excerpt: 'Over 2,400 deliveries completed on this single route in June 2026. We break down the numbers, the best travel times, and why this route has a 99.1% on-time rate.',
    author: 'Dorcas Wona Ndinya',
    authorRole: 'Head of Growth',
    authorInitials: 'DW',
    authorColor: 'bg-green-600',
    date: 'July 5, 2026',
    readTime: '6 min read',
    featured: false,
    tags: ['Routes', 'Kenya', 'Data'],
    accentColor: 'from-green-500/20 to-green-500/5',
    borderColor: 'border-green-200',
  },
  {
    id: 'guarantor-system-deep-dive',
    category: 'Safety & Trust',
    title: 'Why Our Guarantor System Is the Backbone of MuyuRide\'s Trust',
    excerpt: 'Every traveler on MuyuRide has a verified guarantor — a real person who vouches for them. Here\'s why this single feature has kept our fraud rate at near-zero.',
    author: 'Martha Wangui',
    authorRole: 'Head of Trust & Safety',
    authorInitials: 'MW',
    authorColor: 'bg-[#F59E0B]',
    date: 'July 2, 2026',
    readTime: '8 min read',
    featured: false,
    tags: ['Trust', 'Verification', 'Safety'],
    accentColor: 'from-teal-500/20 to-teal-500/5',
    borderColor: 'border-teal-200',
  },
  {
    id: 'live-tracking-launch',
    category: 'Product Updates',
    title: 'Introducing Real-Time GPS Tracking for Every MuyuRide Delivery',
    excerpt: 'Starting today, every MuyuRide delivery includes live GPS tracking. Watch your parcel move across Kenya in real time — from pickup to drop-off.',
    author: 'Zac Munga',
    authorRole: 'CEO & Co-Founder',
    authorInitials: 'ZM',
    authorColor: 'bg-[#0D9488]',
    date: 'June 28, 2026',
    readTime: '4 min read',
    featured: false,
    tags: ['Product', 'Tracking', 'New Feature'],
    accentColor: 'from-purple-500/20 to-purple-500/5',
    borderColor: 'border-purple-200',
  },
  {
    id: 'student-kyc-launch',
    category: 'Product Updates',
    title: 'Student KYC: Verified University Travelers Now Get Priority Matching',
    excerpt: 'Students traveling between campuses and home counties are now a verified category on MuyuRide. Here\'s what it means for senders and student travelers.',
    author: 'Zac Munga',
    authorRole: 'CEO & Co-Founder',
    authorInitials: 'ZM',
    authorColor: 'bg-[#0D9488]',
    date: 'June 20, 2026',
    readTime: '5 min read',
    featured: false,
    tags: ['Students', 'KYC', 'Product'],
    accentColor: 'from-rose-500/20 to-rose-500/5',
    borderColor: 'border-rose-200',
  },
  {
    id: 'packaging-guide',
    category: 'Sender Guides',
    title: 'The Definitive Guide to Packaging Your Parcel for a MuyuRide Delivery',
    excerpt: 'Bad packaging is the #1 cause of parcel damage on peer-to-peer deliveries. This guide covers everything from electronics to food to documents — with photos.',
    author: 'Martha Wangui',
    authorRole: 'Head of Trust & Safety',
    authorInitials: 'MW',
    authorColor: 'bg-[#F59E0B]',
    date: 'June 15, 2026',
    readTime: '9 min read',
    featured: false,
    tags: ['Packaging', 'Tips', 'Sender'],
    accentColor: 'from-amber-500/20 to-amber-500/5',
    borderColor: 'border-amber-200',
  },
  {
    id: 'muyuride-2025-year-in-review',
    category: 'Company News',
    title: 'MuyuRide 2025: 31,400 Deliveries, 47 Counties, Zero Lost Parcels',
    excerpt: 'We\'re sharing everything — the numbers, the milestones, the failures, and what we\'re building in 2026. This is our most honest company update yet.',
    author: 'Zac Munga',
    authorRole: 'CEO & Co-Founder',
    authorInitials: 'ZM',
    authorColor: 'bg-[#0D9488]',
    date: 'January 5, 2026',
    readTime: '12 min read',
    featured: false,
    tags: ['Company', 'Milestones', 'Annual Review'],
    accentColor: 'from-slate-500/20 to-slate-500/5',
    borderColor: 'border-slate-200',
  },
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = posts?.filter((p) => {
    const matchCat = activeCategory === 'All' || p?.category === activeCategory;
    const matchSearch = searchQuery === '' || p?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || p?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchCat && matchSearch;
  });

  const featuredPost = posts?.find((p) => p?.featured);
  const regularPosts = filtered?.filter((p) => !p?.featured || activeCategory !== 'All' || searchQuery !== '');

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2B3E] to-[#0A1628]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#0D9488]/10 blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-[#2563EB]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0D9488]/15 border border-[#0D9488]/30">
            <svg className="w-4 h-4 text-[#4DD9CC]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <span className="text-[#4DD9CC] text-sm font-bold uppercase tracking-widest">MuyuRide Blog</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            Stories from<br />
            <span className="text-[#4DD9CC]">Kenya's delivery network.</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Tips for travelers and senders, product updates, route guides, and honest stories from the MuyuRide team.
          </p>
          {/* Search */}
          <div className="max-w-md mx-auto pt-2">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-[#0D9488]/50 focus:bg-white/15 transition-all text-sm"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>
      {/* Categories */}
      <section className="py-6 bg-[#F8FAFC] border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories?.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-[#0D9488] text-white shadow-[0_2px_8px_rgba(13,148,136,0.3)]'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-[#0D9488] hover:text-[#0D9488]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* Featured post */}
      {featuredPost && activeCategory === 'All' && searchQuery === '' && (
        <section className="py-12 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-5">Featured Article</p>
            <Link href={`/blog/${featuredPost?.id}`} className="group block">
              <div className={`rounded-3xl border ${featuredPost?.borderColor} bg-gradient-to-br ${featuredPost?.accentColor} p-8 lg:p-12 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1`}>
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                  <div className="space-y-5">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-full bg-[#0D9488] text-white text-xs font-bold">{featuredPost?.category}</span>
                      <span className="text-xs text-slate-500">{featuredPost?.readTime}</span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-extrabold text-[#0F172A] leading-tight group-hover:text-[#0D9488] transition-colors duration-300">
                      {featuredPost?.title}
                    </h2>
                    <p className="text-slate-600 leading-relaxed text-lg">{featuredPost?.excerpt}</p>
                    <div className="flex items-center gap-3 pt-2">
                      <div className={`w-10 h-10 rounded-full ${featuredPost?.authorColor} flex items-center justify-center text-white font-extrabold text-sm`}>
                        {featuredPost?.authorInitials}
                      </div>
                      <div>
                        <div className="font-bold text-[#0F172A] text-sm">{featuredPost?.author}</div>
                        <div className="text-xs text-slate-500">{featuredPost?.date}</div>
                      </div>
                    </div>
                  </div>
                  <div className="hidden lg:flex items-center justify-center">
                    <div className="w-64 h-64 rounded-3xl bg-white/60 border border-white/80 flex items-center justify-center shadow-xl">
                      <div className="text-center space-y-3">
                        <div className="text-7xl">💼</div>
                        <div className="text-2xl font-extrabold text-[#0F172A]">KES 5,000</div>
                        <div className="text-sm text-slate-500">per trip potential</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}
      {/* Post grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered?.length === 0 ? (
            <div className="text-center py-20 space-y-4">
              <div className="text-6xl">🔍</div>
              <h3 className="text-xl font-extrabold text-[#0F172A]">No articles found</h3>
              <p className="text-slate-500">Try a different search term or category.</p>
              <button onClick={() => { setSearchQuery(''); setActiveCategory('All'); }} className="px-5 py-2.5 rounded-xl bg-[#0D9488] text-white font-bold text-sm hover:bg-[#0F766E] transition-colors">
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {(activeCategory !== 'All' || searchQuery !== '') && (
                <p className="text-sm text-slate-500 mb-6">
                  {filtered?.length} article{filtered?.length !== 1 ? 's' : ''} found
                  {activeCategory !== 'All' ? ` in "${activeCategory}"` : ''}
                  {searchQuery ? ` for "${searchQuery}"` : ''}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(activeCategory === 'All' && searchQuery === '' ? regularPosts : filtered)?.map((post) => (
                  <Link key={post?.id} href={`/blog/${post?.id}`} className="group block">
                    <article className={`h-full rounded-2xl border ${post?.borderColor} bg-gradient-to-br ${post?.accentColor} p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 rounded-full bg-white/80 text-[#0F172A] text-xs font-bold border border-white/60">{post?.category}</span>
                        <span className="text-xs text-slate-500">{post?.readTime}</span>
                      </div>
                      <h3 className="text-lg font-extrabold text-[#0F172A] leading-tight mb-3 group-hover:text-[#0D9488] transition-colors duration-200 flex-1">
                        {post?.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-5 line-clamp-3">{post?.excerpt}</p>
                      <div className="flex items-center gap-3 mt-auto">
                        <div className={`w-8 h-8 rounded-full ${post?.authorColor} flex items-center justify-center text-white font-extrabold text-xs shrink-0`}>
                          {post?.authorInitials}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-[#0F172A] text-xs truncate">{post?.author}</div>
                          <div className="text-xs text-slate-500">{post?.date}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {post?.tags?.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-full bg-white/60 text-slate-600 text-xs font-medium">#{tag}</span>
                        ))}
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      {/* Newsletter CTA */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#0A1628] to-[#0D2B3E] p-10 text-center space-y-5">
            <div className="text-4xl">📬</div>
            <h2 className="text-3xl font-extrabold text-white">Get new articles in your inbox</h2>
            <p className="text-slate-300">Tips for travelers, guides for senders, and product updates — delivered weekly.</p>
            <div className="flex gap-2 max-w-sm mx-auto">
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-[#0D9488]/50 text-sm"
              />
              <button className="px-5 py-3 rounded-xl bg-[#0D9488] text-white font-bold text-sm hover:bg-[#0F766E] transition-colors shrink-0">
                Subscribe
              </button>
            </div>
            <p className="text-xs text-slate-500">No spam. Unsubscribe anytime. ~1 email per week.</p>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
